/**
 * The dollar-facing payments model.
 *
 * A3.09's requirement is that the primary tenant view carries no blockchain
 * vocabulary: a balance reads `$25.37`, never `25.37 DDT`. The cleanest way to
 * guarantee that is for this module to never receive a denom in the first
 * place — the billing API sends cents and a pre-formatted dollar string, and
 * the only chain-shaped values in these types live on the redemption object,
 * where the holder genuinely needs them to sign.
 *
 * Amounts are integers of cents throughout. `number` is safe here in a way it
 * is not on the server: JavaScript integers are exact to 2^53, and the funding
 * ceiling is 100,000 cents.
 */

export type ComplianceStatus = 'none' | 'pending' | 'approved' | 'rejected'

export type FundingStatus = 'created' | 'settled' | 'mint_pending' | 'minted' | 'failed' | 'cancelled'

export type RedemptionStatus =
  | 'created'
  | 'burn_proven'
  | 'payout_pending'
  | 'paid'
  | 'manual_review'

export interface PaymentsSummaryDto {
  wallet_address: string
  available_cents: number | null
  available_display: string | null
  compliance_status: ComplianceStatus
  pending_funding: boolean
  payment_provider?: 'fake' | 'stripe'
}

export interface FundingIntentDto {
  id: string
  amount_cents: number
  amount_display: string
  status: FundingStatus
  checkout_url: string | null
  created_at: string | null
}

export interface RedemptionDto {
  id: string
  amount_cents: number
  amount_display: string
  status: RedemptionStatus
  /** Needed to sign MsgRedeem; deliberately absent from the balance view. */
  operation_id: string
  amount_micro: string
  created_at: string | null
}

export interface ActivityRowDto {
  id: string
  kind: 'funding' | 'redemption'
  amount_display: string
  status: string
  created_at: string | null
}

export interface PaymentsActivityDto {
  funding: ActivityRowDto[]
  redemptions: ActivityRowDto[]
}

/**
 * `2537` -> `$25.37`, by integer arithmetic only.
 *
 * The server already sends a formatted string and the UI prefers it. This
 * exists for values the UI computes itself (a form's live preview), and it is
 * written the same way the server writes it so the two cannot disagree.
 */
export function formatCents(cents: number): string {
  if (!Number.isFinite(cents) || !Number.isInteger(cents)) return '—'
  const sign = cents < 0 ? '-' : ''
  const absolute = Math.abs(cents)
  const whole = Math.floor(absolute / 100)
  const part = absolute % 100
  return `${sign}$${whole.toLocaleString('en-US')}.${String(part).padStart(2, '0')}`
}

/**
 * `"25.37"` -> `2537`, or null when the input is not a whole number of cents.
 *
 * Parsed as a string rather than via `parseFloat` and a multiply: `25.37 * 100`
 * is `2536.9999999999995`, and rounding that is exactly the class of bug the
 * whole money path is built to avoid.
 */
export function parseDollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, '').replace(/,/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null
  const [whole, fraction = ''] = trimmed.split('.')
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(cents) ? cents : null
}

/**
 * A key identifying one funding *attempt*, not one request.
 *
 * The caller is expected to hold it for as long as the attempt lasts and to
 * replace it when the amount changes. That is what makes it worth having: the
 * console's HTTP client aborts at 12 seconds, and billing may well have created
 * the intent by then, so a tenant who tries again after a timeout must arrive
 * with the same key or they leave an abandoned intent row behind every time.
 */
export function newIdempotencyKey(): string {
  const source = globalThis.crypto
  if (source && typeof source.randomUUID === 'function') return source.randomUUID()

  // `randomUUID` is secure-context-only, and the console is served over plain
  // HTTP on the lab — so this is the ordinary path there, not a fallback for
  // exotic browsers. It has to produce a real UUID: billing parses the key with
  // `uuid.UUID()` and answers 400 for anything else, which would have made every
  // funding attempt fail on exactly the deployment this feature is for.
  // `getRandomValues` is not secure-context-gated, so it is available where
  // `randomUUID` is not.
  const bytes = new Uint8Array(16)
  if (source && typeof source.getRandomValues === 'function') source.getRandomValues(bytes)
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6]! & 0x0f) | 0x40
  bytes[8] = (bytes[8]! & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * A URL the browser may be sent to, or a refusal.
 *
 * Hosted checkout means handing the tenant to somewhere this console does not
 * control, and `location.assign` will execute a `javascript:` URL in this
 * page's own origin. The proxy already allowlists where a processor may send a
 * browser *back*; this is the same check in the inbound direction, which was
 * missing. The current sandbox processor returns a fixed https URL with no
 * tenant input in it — the point is that the first real processor adapter does
 * not get to be the thing that decides this.
 */
export function externalPaymentUrl(value: string | null | undefined): string {
  let parsed: URL
  try {
    parsed = new URL(value ?? '')
  } catch {
    throw new Error('That payment link was not usable.')
  }
  if (parsed.protocol !== 'https:') throw new Error('That payment link was not usable.')
  return parsed.href
}

/**
 * The sandbox's mock pages, which are served by this console.
 *
 * ADR-003 §1.4 defers both vendors, so in sandbox there is no third-party
 * origin to hand the browser to and `externalPaymentUrl` — correctly — refuses
 * every candidate. The prefix is exactly `/sandbox/` and nothing else: a
 * general "relative paths are fine" rule would let a processor answer
 * `/app/ops` and drive the tenant somewhere inside their own session, which is
 * why `externalPaymentUrl` refuses `/app/billing` and still does.
 */
const SANDBOX_MOCK_PREFIX = '/sandbox/'

/**
 * Where a hosted flow may send the browser: the sandbox mock, or a real vendor.
 *
 * One function so neither call site has to remember which case it is in — the
 * whole point of `BILLING_SANDBOX_MOCK_BASE` is that swapping the mock for a
 * vendor changes configuration and nothing else.
 */
export function paymentDestination(value: string | null | undefined): string {
  if (typeof value === 'string' && value.startsWith(SANDBOX_MOCK_PREFIX) && !value.startsWith('//')) {
    return value
  }
  return externalPaymentUrl(value)
}

// ADR-003 §1.2, mirrored so the form can refuse before a round trip. The server
// refuses too; this is a courtesy, not the control.
export const MIN_FUNDING_CENTS = 100
export const MAX_FUNDING_CENTS = 100_000
export const MIN_REDEMPTION_CENTS = 100
export const MAX_REDEMPTION_CENTS = 100_000

export interface FundingPhase {
  /** What the tenant is told. Never mentions minting, denoms or heights. */
  label: string
  tone: 'neutral' | 'progress' | 'positive' | 'warning'
  /** True while the money is real but not yet spendable. */
  inFlight: boolean
}

/**
 * The single place that decides what a funding state is called.
 *
 * The distinction A3.09 insists on: "Payment received; adding funds" while the
 * mint is pending, and "Funds available" only once the chain has confirmed it.
 * Saying "available" a moment early would be a lie the tenant could act on.
 */
export function fundingPhase(status: FundingStatus): FundingPhase {
  switch (status) {
    case 'created':
      return { label: 'Awaiting payment', tone: 'neutral', inFlight: false }
    case 'settled':
    case 'mint_pending':
      return { label: 'Payment received; adding funds', tone: 'progress', inFlight: true }
    case 'minted':
      return { label: 'Funds available', tone: 'positive', inFlight: false }
    case 'failed':
      return { label: 'Payment failed', tone: 'warning', inFlight: false }
    case 'cancelled':
      return { label: 'Payment cancelled', tone: 'neutral', inFlight: false }
    default:
      return { label: 'Unknown', tone: 'neutral', inFlight: false }
  }
}

export function redemptionPhase(status: RedemptionStatus): FundingPhase {
  switch (status) {
    case 'created':
      return { label: 'Awaiting your approval', tone: 'neutral', inFlight: false }
    case 'burn_proven':
    case 'payout_pending':
      return { label: 'Approved; sending your money', tone: 'progress', inFlight: true }
    case 'paid':
      return { label: 'Sent', tone: 'positive', inFlight: false }
    case 'manual_review':
      return { label: 'Needs review — we will be in touch', tone: 'warning', inFlight: false }
    default:
      return { label: 'Unknown', tone: 'neutral', inFlight: false }
  }
}

export interface WithdrawalCheck {
  ok: boolean
  reason: string | null
}

/** Client-side sanity for the withdrawal form. The server decides. */
export function checkWithdrawal(cents: number | null, availableCents: number | null): WithdrawalCheck {
  if (cents === null) return { ok: false, reason: 'Enter an amount like 10.00' }
  if (cents < MIN_REDEMPTION_CENTS)
    return { ok: false, reason: `The minimum is ${formatCents(MIN_REDEMPTION_CENTS)}` }
  if (cents > MAX_REDEMPTION_CENTS)
    return { ok: false, reason: `The maximum is ${formatCents(MAX_REDEMPTION_CENTS)}` }
  if (availableCents !== null && cents > availableCents)
    return { ok: false, reason: `You have ${formatCents(availableCents)} available` }
  return { ok: true, reason: null }
}

export function checkFunding(cents: number | null): WithdrawalCheck {
  if (cents === null) return { ok: false, reason: 'Enter an amount like 25.37' }
  if (cents < MIN_FUNDING_CENTS)
    return { ok: false, reason: `The minimum is ${formatCents(MIN_FUNDING_CENTS)}` }
  if (cents > MAX_FUNDING_CENTS)
    return { ok: false, reason: `The maximum is ${formatCents(MAX_FUNDING_CENTS)}` }
  return { ok: true, reason: null }
}
